import { toValue, type MaybeRefOrGetter } from 'vue'
import { serializeSeoJsonLd, type SeoJsonLdObject } from '~/utils/seoJsonLd'

export type SeoBreadcrumbInput = {
  label: string
  route?: string
}

const SEO_SITE_NAME = 'WikiBit'
const SEO_LOGO_PATH = '/favicon.ico'

/** 将站内路径或外部图片地址转换为绝对 URL，非法值返回空字符串。 */
export const resolveSeoUrl = (value: string | undefined, baseUrl: string) => {
  if (!value) {
    return ''
  }

  try {
    return new URL(value, baseUrl).href
  } catch {
    return ''
  }
}

/** 将页面面包屑转换为 Schema.org BreadcrumbList。 */
export const createSeoBreadcrumbList = (items: SeoBreadcrumbInput[], baseUrl: string) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items
    .filter((item) => item.label)
    .map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.route
        ? {
            item: resolveSeoUrl(item.route, baseUrl),
          }
        : {}),
    })),
})

/** 生成站点统一的 Schema.org 组织实体，供文章、评论和站点实体复用。 */
export const createSeoOrganization = (siteUrl: string) => ({
  '@type': 'Organization',
  '@id': `${siteUrl}/#organization`,
  name: SEO_SITE_NAME,
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: resolveSeoUrl(SEO_LOGO_PATH, `${siteUrl}/`),
  },
})

/** 生成列表页统一引用的站点实体。 */
export const createSeoWebSite = (siteUrl: string) => ({
  '@type': 'WebSite',
  '@id': `${siteUrl}/#website`,
  url: siteUrl,
  name: SEO_SITE_NAME,
})

/** 返回当前路由不含查询参数的规范绝对 URL。 */
export const useSeoCanonicalUrl = () => {
  const route = useRoute()
  const requestUrl = useRequestURL()
  const requestHeaders = import.meta.server ? useRequestHeaders(['host', 'x-forwarded-host', 'x-forwarded-proto']) : {}

  return computed(() => {
    let origin = requestUrl.origin

    if (import.meta.server) {
      const forwardedHost = requestHeaders['x-forwarded-host']?.split(',')[0]?.trim()
      const host = forwardedHost || requestHeaders.host?.trim() || requestUrl.host
      const forwardedProtocol = requestHeaders['x-forwarded-proto']?.split(',')[0]?.trim()
      const protocol = forwardedProtocol === 'https' || requestUrl.protocol === 'https:' ? 'https' : 'http'
      origin = `${protocol}://${host}`
    } else {
      origin = window.location.origin
    }

    const pathname = route.path === '/' ? '/' : route.path.replace(/\/+$/, '')
    return new URL(pathname, origin).href
  })
}

/** 将 JSON-LD 以 SSR 可见的 application/ld+json script 注入页面 head。 */
export const useSeoJsonLd = (schema: MaybeRefOrGetter<SeoJsonLdObject | null | undefined>, key = 'seo-json-ld') => {
  useHead(() => {
    const resolvedSchema = toValue(schema)

    return {
      script: resolvedSchema
        ? [
            {
              key,
              type: 'application/ld+json',
              innerHTML: serializeSeoJsonLd(resolvedSchema),
            },
          ]
        : [],
    }
  })
}
